// The campaign's NPCs at the table (issue 1056), the client's half. The pool lives on the site; the server reads it with
// the DM's own bearer, so every call here is to THIS origin (`requestJson`, so `authFetch`). An NPC is placed as a
// compendium result is placed (the "Place <name>" dialog), by its id.
import { requestJson } from './api';
import { siteUrl } from './session';

/** One NPC of the pool: its name and its stat block's name and address (its hover card, issue 1585), never its notes. */
export interface CampaignNpc { id: string; name: string; hasStatBlock: boolean; statBlockName: string | null; statBlock: { segment: string; slug: string } | null }

type Refused = { kind: 'refused'; reason: string } | { kind: 'error' };
export type NpcsAnswer = { kind: 'ok'; campaignId: string; npcs: CampaignNpc[] } | Refused;
export type NpcPlaced = { kind: 'ok'; id: string; combatant?: string } | Refused;

export interface NpcPlaceAsk { x: number; y: number; name: string; hidden: boolean; tracker: boolean }

const enc = encodeURIComponent;
const isNpc = (v: unknown): v is CampaignNpc => {
  const o = v as Record<string, unknown> | null;
  return !!o && typeof o.id === 'string' && typeof o.name === 'string' && typeof o.hasStatBlock === 'boolean'
    && (o.statBlockName === null || typeof o.statBlockName === 'string');
};
const addressOf = (v: unknown): { segment: string; slug: string } | null => {
  const o = v as Record<string, unknown> | null | undefined;
  return o && typeof o === 'object' && typeof o.segment === 'string' && typeof o.slug === 'string' && o.segment && o.slug ? { segment: o.segment, slug: o.slug } : null;
};
const refusedOr = (status: number, body: unknown): Refused => {
  const reason = (body as { reason?: unknown } | null)?.reason;
  if (typeof reason === 'string') return { kind: 'refused', reason };
  if (status === 403) return { kind: 'refused', reason: "That isn't yours to do." };
  if (status === 400) return { kind: 'refused', reason: 'That name or place cannot be used.' };
  return { kind: 'error' };
};

/** The campaign's page on the site, where its NPCs are added and edited. */
export const campaignPageUrl = (campaignId: string): string => `${siteUrl()}/campaigns?id=${enc(campaignId)}`;

/** The campaign's NPC pool. */
export async function listNpcs(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<NpcsAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/npcs`, {}, fetchImpl);
  const b = (body ?? {}) as { campaignId?: unknown; npcs?: unknown };
  if (status === 200 && typeof b.campaignId === 'string' && Array.isArray(b.npcs)) {
    return { kind: 'ok', campaignId: b.campaignId, npcs: b.npcs.filter(isNpc).map((n) => ({ id: n.id, name: n.name, hasStatBlock: n.hasStatBlock, statBlockName: n.statBlockName, statBlock: addressOf((n as { statBlock?: unknown }).statBlock) })) };
  }
  return refusedOr(status, body);
}

/** Place one on the scene the DM is looking at. A refusal carries the server's own words. */
export async function placeNpc(campaignId: string, npcId: string, ask: NpcPlaceAsk, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<NpcPlaced> {
  const body = { ...ask, x: Math.round(ask.x), y: Math.round(ask.y), ...(sceneId ? { scene_id: sceneId } : {}) };
  const { status, body: got } = await requestJson(`/api/tables/${enc(campaignId)}/npcs/${enc(npcId)}/place`, { method: 'POST', body: JSON.stringify(body) }, fetchImpl);
  const b = (got ?? {}) as { id?: unknown; combatant?: unknown };
  if ((status === 200 || status === 201) && typeof b.id === 'string') return { kind: 'ok', id: b.id, ...(typeof b.combatant === 'string' ? { combatant: b.combatant } : {}) };
  return refusedOr(status, got);
}

export type NpcSaved = { kind: 'ok'; id: string; name: string } | Refused;

/**
 * Save an NPC to the campaign's pool (issue 1157): a name and a note, which the site keeps as the NPC's notes (the DM's
 * alone). The server carries it to the site's own create with the DM's bearer; nothing is told to the table.
 */
export async function saveNpc(campaignId: string, npc: { name: string; note: string }, fetchImpl: typeof fetch = fetch): Promise<NpcSaved> {
  const note = npc.note.trim();
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/npcs`, { method: 'POST', body: JSON.stringify({ name: npc.name.trim(), ...(note ? { note } : {}) }) }, fetchImpl);
  const b = ((body ?? {}) as { npc?: { id?: unknown; name?: unknown } }).npc ?? {};
  if (status === 201 && typeof b.id === 'string' && typeof b.name === 'string') return { kind: 'ok', id: b.id, name: b.name };
  return refusedOr(status, body);
}

/** The pool narrowed to the names holding what was typed, ignoring case. */
export const npcsMatching = (npcs: CampaignNpc[], q: string): CampaignNpc[] => {
  const t = q.trim().toLowerCase();
  return t ? npcs.filter((n) => n.name.toLowerCase().includes(t)) : npcs;
};
